import { useMemo } from "react";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import { navigate, useCurrentUrl } from "../../lib/urls";
import type { RouteNode } from "../../lib/urls/types";
import {
  matchesScope,
  type MonitoringScope,
  scopeFromParam,
} from "./monitoringScope";

export type { MonitoringScope };

// scope is folded onto each monitoring child from the parent route at runtime,
// so it isn't visible in a child's literal pattern type — any monitoring route
// works here.
export type ScopedRoute = RouteNode;

export function useMonitoringScope(route: ScopedRoute): {
  scope: MonitoringScope;
  setScope: (scope: MonitoringScope) => void;
  matchesSpace: (spaceId: string | null | undefined) => boolean;
  personalIds: ReadonlySet<string>;
  teamIds: ReadonlySet<string>;
} {
  const url = useCurrentUrl(route) as Record<string, string | undefined>;
  const scope = scopeFromParam(url.scope);
  const [spaceRows] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );

  return useMemo(() => {
    const personalIds = new Set<string>();
    const teamIds = new Set<string>();
    for (const space of spaceRows ?? []) {
      (space.mode === "personal" ? personalIds : teamIds).add(space.id);
    }
    return {
      scope,
      setScope: (next) =>
        navigate(
          null,
          { scope: next === "both" ? "" : next },
          { replace: true }
        ),
      matchesSpace: (spaceId) =>
        matchesScope(scope, spaceId, personalIds, teamIds),
      personalIds,
      teamIds,
    };
  }, [scope, spaceRows]);
}
